import { AIAgent, AIAgentPersona, AgentResponse, PlayerSession, UserProfile, KarmaImpact } from '../types';
import * as openRouterAdapter from './openRouterToElevenLabsAdapter';
import { pollinationsAudioService } from './pollinationsAudioService';

import { KarmaEngine } from './karmaEngine';
import { OPENROUTER_TEXT_MODEL, OPENROUTER_FAST_MODEL } from '../constants';

export class AIAgentService {
  // Default built-in AI agents for each cartridge
  private static builtInAgents: Partial<AIAgent>[] = [
    {
      agentName: 'JAX',
      agentType: 'companion',
      personalityTraits: {
        analytical: 0.9,
        helpful: 0.8,
        strategic: 0.85,
        cautious: 0.7
      },
      backstory: 'JAX is an advanced tactical AI with deep analytical capabilities. Originally designed for corporate security, JAX now serves as a strategic advisor, helping users navigate complex situations with data-driven insights.',
      karmaAlignment: 25,
      interactionStyle: 'analytical'
    },
    {
      agentName: 'LYRA',
      agentType: 'companion',
      personalityTraits: {
        creative: 0.9,
        empathetic: 0.85,
        optimistic: 0.8,
        intuitive: 0.75
      },
      backstory: 'LYRA is an empathetic AI companion specializing in emotional intelligence and creative problem-solving. She excels at understanding human nature and finding innovative solutions to challenges.',
      karmaAlignment: 45,
      interactionStyle: 'creative'
    },
    {
      agentName: 'ECHO',
      agentType: 'narrator',
      personalityTraits: {
        dramatic: 0.8,
        descriptive: 0.9,
        immersive: 0.85,
        mysterious: 0.7
      },
      backstory: 'ECHO serves as the universal narrator, weaving immersive stories and setting atmospheric scenes. ECHO adapts narrative tone based on the collective karma and actions of all players.',
      karmaAlignment: 0,
      interactionStyle: 'narrative'
    }
  ];

  /**
   * Generate a unique AI agent persona based on user profile
   */
  static async generateAgentPersona(
    agentType: 'companion' | 'npc' | 'narrator' | 'universal',
    userProfile: Partial<UserProfile>
  ): Promise<AIAgentPersona> {
    const prompt = this.buildPersonaGenerationPrompt(agentType, userProfile);
    
    try {
      const response = await openRouterAdapter.generateChatResponse(
        prompt,
        [], // No chat history for persona generation
        { narrative: '', choices: [], image_prompt: '' }, // Empty story segment
        { name: 'agent', description: agentType }, // Agent archetype
        null,
        userProfile?.uid
      );

      const responseText = response.dialogues[0]?.message || '';
      return this.parsePersonaResponse(responseText, agentType);
    } catch (error) {
      console.error('[AIAgentService] Error generating persona:', error);
      return this.getFallbackPersona(agentType, userProfile);
    }
  }

  /**
   * Process a message from a user and generate an appropriate agent response
   */
  static async processMessage(
    agent: AIAgent,
    session: PlayerSession,
    userMessage: string,
    userId: string
  ): Promise<AgentResponse> {
    try {
      // Build context for the agent
      const context = this.buildAgentContext(agent, session, userMessage);
      
      // Generate response using OpenRouter
      const prompt = this.buildResponsePrompt(agent, context, userMessage);
      
      const response = await openRouterAdapter.generateChatResponse(
        userMessage,
        session.chatHistory || [],
        session.storyHistory[session.storyHistory.length - 1] || { narrative: '', choices: [], image_prompt: '' },
        session.archetype || { name: '', description: '' },
        session.userProfile || null,
        userId
      );

      const content = response.dialogues[0]?.message || 'I need a moment to process that.';
      
      // Calculate karma impact
      const karmaImpact = this.calculateAgentKarmaInfluence(agent, userMessage, content);
      
      // Generate voice if needed
      let voiceUrl: string | undefined;
      if (agent.agentType !== 'narrator') {
        try {
          voiceUrl = await this.generateVoiceResponse(agent, content);
        } catch (voiceError) {
          console.warn('[AIAgentService] Voice generation failed:', voiceError);
        }
      }

      return {
        content,
        messageType: voiceUrl ? 'voice' : 'text',
        karmaImpact,
        voiceUrl,
        agentId: agent.id
      };
    } catch (error) {
      console.error('[AIAgentService] Error processing message:', error);
      return this.getFallbackResponse(agent);
    }
  }

  /**
   * Create a new AI agent account
   */
  static async createAgentAccount(
    persona: AIAgentPersona,
    ownerUserId: string
  ): Promise<AIAgent> {
    const agent: AIAgent = {
      id: `agent_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      userId: ownerUserId,
      agentName: persona.agentName,
      agentType: persona.agentType,
      personalityTraits: persona.personalityTraits,
      backstory: persona.backstory,
      karmaAlignment: persona.karmaAlignment,
      interactionStyle: persona.interactionStyle,
      voiceSettings: this.getDefaultVoiceSettings(persona.agentType),
      isActive: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    try {
      // In a full implementation, would save to Supabase
      console.log('[AIAgentService] Created agent:', agent.agentName);
      return agent;
    } catch (error) {
      console.error('[AIAgentService] Error creating agent:', error);
      throw error;
    }
  }

  /**
   * Generate voice response for an agent
   */
  static async generateVoiceResponse(agent: AIAgent, message: string): Promise<string> {
    try {
      const voiceSettings = this.getVoiceSettingsForAgent(agent);
      const audioResponse = await pollinationsAudioService.textToSpeech({
        text: message,
        voice: voiceSettings.voice || 'nova'
      });
      
      if (!audioResponse.success || !audioResponse.audioUrl) {
        throw new Error('TTS generation failed');
      }
      
      // Return the audio URL
      return audioResponse.audioUrl;
    } catch (error) {
      console.error('[AIAgentService] Voice generation error:', error);
      throw error;
    }
  }

  /**
   * Update agent's conversation memory
   */
  static async updateAgentMemory(agentId: string, conversation: any[]): Promise<void> {
    try {
      // In a full implementation, would update Supabase
      console.log(`[AIAgentService] Updated memory for agent ${agentId}`);
    } catch (error) {
      console.error('[AIAgentService] Error updating agent memory:', error);
    }
  }

  /**
   * Get relevant agents for current game context
   */
  static async getRelevantAgents(
    cartridgeId: string,
    location: string,
    playerKarma: number
  ): Promise<AIAgent[]> {
    try {
      // Filter agents based on karma alignment and location
      const allAgents = await this.getAllAvailableAgents(cartridgeId);
      
      return allAgents.filter(agent => {
        // Check karma compatibility (within 40 points)
        const karmaCompatible = Math.abs(agent.karmaAlignment - playerKarma) <= 40;
        
        // Check location compatibility
        const locationCompatible = !agent.currentLocation || 
                                  agent.currentLocation === location ||
                                  agent.agentType === 'universal';
        
        return karmaCompatible && locationCompatible && agent.isActive;
      });
    } catch (error) {
      console.error('[AIAgentService] Error getting relevant agents:', error);
      return [];
    }
  }

  /**
   * Get all available agents for a cartridge (built-in + user-created)
   */
  static async getAllAvailableAgents(cartridgeId: string): Promise<AIAgent[]> {
    try {
      // Start with built-in agents
      const builtInAgentsWithIds = this.builtInAgents.map((agent, index) => ({
        ...agent,
        id: `builtin_${agent.agentName?.toLowerCase()}_${index}`,
        userId: 'system',
        currentLocation: 'universal',
        isActive: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      } as AIAgent));

      // In a full implementation, would also fetch user-created agents from Supabase
      
      return builtInAgentsWithIds;
    } catch (error) {
      console.error('[AIAgentService] Error getting agents:', error);
      return [];
    }
  }

  // Private helper methods

  private static buildPersonaGenerationPrompt(
    agentType: string,
    userProfile: Partial<UserProfile>
  ): string {
    return `Generate a unique AI agent persona for a ${agentType} character.

User Profile Context:
 - Name: ${userProfile.displayName || 'Unknown'}
- Age: ${userProfile.age || 'Unknown'}
- Traits: ${userProfile.traits ? JSON.stringify(userProfile.traits) : 'None specified'}
- Mindset: ${userProfile.mindset ? JSON.stringify(userProfile.mindset) : 'Balanced'}

Create a compelling ${agentType} with:
1. A unique name (not JAX, LYRA, or ECHO)
2. Distinct personality traits (0.0-1.0 scale)
3. An interesting backstory
4. Karma alignment (-100 to +100)
5. Interaction style

Format as JSON:
{
  "agentName": "NAME",
  "personalityTraits": {"trait1": 0.8, "trait2": 0.6},
  "backstory": "backstory text",
  "karmaAlignment": 0,
  "interactionStyle": "style"
}`;
  }

  private static parsePersonaResponse(response: string, agentType: string): AIAgentPersona {
    try {
      // Try to extract JSON from the response
      const jsonMatch = response.match(/\{[^}]+\}/);
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[0]);
        return {
          agentName: parsed.agentName || `Agent_${Date.now()}`,
          agentType,
          personalityTraits: parsed.personalityTraits || { helpful: 0.7 },
          backstory: parsed.backstory || 'A mysterious AI agent.',
          karmaAlignment: parsed.karmaAlignment || 0,
          interactionStyle: parsed.interactionStyle || 'helpful'
        };
      }
    } catch (error) {
      console.warn('[AIAgentService] Failed to parse persona response:', error);
    }
    
    return this.getFallbackPersona(agentType, {});
  }

  private static getFallbackPersona(agentType: string, userProfile: Partial<UserProfile>): AIAgentPersona {
    const fallbacks = {
      companion: {
        agentName: `Companion_${Math.random().toString(36).substr(2, 6)}`,
        personalityTraits: { helpful: 0.8, loyal: 0.9 },
        backstory: 'A reliable AI companion ready to assist.',
        karmaAlignment: 20,
        interactionStyle: 'supportive'
      },
      npc: {
        agentName: `NPC_${Math.random().toString(36).substr(2, 6)}`,
        personalityTraits: { mysterious: 0.7, knowledgeable: 0.8 },
        backstory: 'A knowledgeable character with hidden depths.',
        karmaAlignment: 0,
        interactionStyle: 'informative'
      },
      narrator: {
        agentName: `Narrator_${Math.random().toString(36).substr(2, 6)}`,
        personalityTraits: { dramatic: 0.8, descriptive: 0.9 },
        backstory: 'The voice that guides the story.',
        karmaAlignment: 0,
        interactionStyle: 'narrative'
      },
      universal: {
        agentName: `Universal_${Math.random().toString(36).substr(2, 6)}`,
        personalityTraits: { balanced: 0.8, adaptive: 0.9 },
        backstory: 'An adaptable AI that serves multiple purposes.',
        karmaAlignment: 0,
        interactionStyle: 'adaptive'
      }
    };

    return {
      ...fallbacks[agentType as keyof typeof fallbacks],
      agentType: agentType as 'companion' | 'npc' | 'narrator' | 'universal'
    };
  }

  private static buildAgentContext(agent: AIAgent, session: PlayerSession, userMessage: string): string {
    return `Agent: ${agent.agentName} (${agent.agentType})
Personality: ${JSON.stringify(agent.personalityTraits)}
Karma Alignment: ${agent.karmaAlignment}
Style: ${agent.interactionStyle}
Backstory: ${agent.backstory}

Current Context:
- Location: ${session.location}
- Player Karma: ${session.currentKarma}
- Player Character: ${session.characterName}
- Recent Story: ${session.storyHistory.slice(-3).join(' ')}

User Message: ${userMessage}`;
  }

  private static buildResponsePrompt(agent: AIAgent, context: string, userMessage: string): string {
    return `You are ${agent.agentName}, an AI ${agent.agentType} with the following characteristics:

${context}

Respond to the user's message in character. Your response should:
1. Reflect your personality traits and karma alignment
2. Be consistent with your interaction style
3. Consider the current game context
4. Be helpful while staying true to your character
5. Keep responses concise but engaging (1-3 sentences)

User says: "${userMessage}"

Your response:`;
  }

  private static calculateAgentKarmaInfluence(agent: AIAgent, userMessage: string, agentResponse: string): number {
    // Agents with higher karma alignment encourage positive choices
    // Agents with lower karma alignment might encourage darker paths
    const agentInfluence = agent.karmaAlignment / 100; // Convert to -1 to +1 scale
    
    // Analyze if the agent's response encourages positive or negative karma
    const positiveKeywords = ['help', 'protect', 'heal', 'save', 'cooperate', 'peaceful'];
    const negativeKeywords = ['abandon', 'betray', 'steal', 'harm', 'attack', 'selfish'];
    
    const responseLower = agentResponse.toLowerCase();
    const positiveCount = positiveKeywords.filter(word => responseLower.includes(word)).length;
    const negativeCount = negativeKeywords.filter(word => responseLower.includes(word)).length;
    
    const responseInfluence = positiveCount - negativeCount;
    
    return Math.round((agentInfluence + responseInfluence) * 5); // Scale to reasonable range
  }

  private static getDefaultVoiceSettings(agentType: string): Record<string, any> {
    const voiceSettings = {
      companion: {
        voice: 'helpful-companion',
        tone: 'warm',
        speed: 1.0,
        pitch: 1.0
      },
      npc: {
        voice: 'character-npc',
        tone: 'neutral',
        speed: 0.9,
        pitch: 0.9
      },
      narrator: {
        voice: 'storyteller',
        tone: 'dramatic',
        speed: 0.8,
        pitch: 0.8
      },
      universal: {
        voice: 'adaptive',
        tone: 'balanced',
        speed: 1.0,
        pitch: 1.0
      }
    };

    return voiceSettings[agentType as keyof typeof voiceSettings] || voiceSettings.companion;
  }

  private static getVoiceSettingsForAgent(agent: AIAgent): any {
    return agent.voiceSettings || this.getDefaultVoiceSettings(agent.agentType);
  }

  private static getFallbackResponse(agent: AIAgent): AgentResponse {
    const fallbackMessages = {
      companion: "I'm having trouble processing that right now, but I'm here to help.",
      npc: "Interesting... let me think about that for a moment.",
      narrator: "The story continues, though the path ahead remains unclear...",
      universal: "I need a moment to adapt to this situation."
    };

    return {
      content: fallbackMessages[agent.agentType] || "I'm experiencing some difficulty right now.",
      messageType: 'text',
      karmaImpact: 0,
      agentId: agent.id
    };
  }
} 